import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../helpers/dates";
import {
  extendLocationWithGrants,
  fetchGrantExtendInfo,
} from "../../helpers/grantExtendApi";

/**
 * Обмен наград: 1 × grant 757 = 1 месяц доступа к выбранной локации.
 *
 * После оплаты с birthday человек попадает сюда с /login?id=… /register?id=…
 * и тратит начисленные награды на продление мира.
 */

const PUBLIC = process.env.PUBLIC_URL || "";

const monthsWord = (n) => {
  const v = Math.abs(Number(n)) % 100;
  const v1 = v % 10;
  if (v > 10 && v < 20) return "месяцев";
  if (v1 === 1) return "месяц";
  if (v1 >= 2 && v1 <= 4) return "месяца";
  return "месяцев";
};

const GrantExchangePage = () => {
  const [available, setAvailable] = useState(0);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [months, setMonths] = useState("1");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    fetchGrantExtendInfo()
      .then((data) => {
        if (!alive) return;
        setAvailable(Number(data.available) || 0);
        const list = Array.isArray(data.locations) ? data.locations : [];
        setLocations(list);
        if (list.length && !locationId) {
          setLocationId(String(list[0].id));
        }
      })
      .catch((e) => {
        if (alive) setError(e.message || "Не удалось загрузить данные");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => locations.find((l) => String(l.id) === String(locationId)),
    [locations, locationId]
  );

  const paidTillLabel = selected?.paid_till
    ? formatDate(selected.paid_till)
    : "не задан";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const monthsNum = Number(months);
    if (!locationId) {
      setError("Выберите локацию");
      return;
    }
    if (!Number.isInteger(monthsNum) || monthsNum < 1) {
      setError("Укажите целое число месяцев, не меньше 1");
      return;
    }
    if (monthsNum > available) {
      setError(`Недостаточно наград: доступно ${available}`);
      return;
    }

    setSubmitting(true);
    try {
      const data = await extendLocationWithGrants(locationId, monthsNum);
      setSuccess(data);
      setAvailable(Number(data.available_left) || 0);
      setLocations((prev) =>
        prev.map((l) =>
          String(l.id) === String(data.location_id)
            ? { ...l, paid_till: data.paid_till }
            : l
        )
      );
    } catch (err) {
      setError(err.message || "Не удалось продлить локацию");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fw-page">
      <h1 className="fw-title">Обмен наград</h1>
      <p className="fw-sub">
        Одна награда даёт один месяц доступа к выбранной локации. Создание
        нового мира тоже списывает одну награду.
      </p>

      <div className="fw-form-acts" style={{ marginBottom: 16 }}>
        <Link className="fw-btn" to={`${PUBLIC}/location-list/add`}>
          Создать локацию
        </Link>
      </div>

      {loading && <p className="fw-sec-sub">Загрузка…</p>}

      {!loading && success && (
        <section className="fw-card fw-box">
          <h3>Готово</h3>
          <p className="fw-note">
            {success.message ||
              `Вы успешно продлили локацию на ${success.months} ${monthsWord(
                success.months
              )}.`}
          </p>
          {success.paid_till ? (
            <p className="fw-note" style={{ marginBottom: 0 }}>
              Доступ до: <b>{formatDate(success.paid_till)}</b>
            </p>
          ) : null}
        </section>
      )}

      {!loading && !success && (
        <section className="fw-card fw-box">
          <h3>Продление локации</h3>
          <p className="fw-note">
            Доступно наград: <b>{available}</b>
            {available === 0
              ? " — сначала нужна покупка или начисление наград."
              : null}
          </p>

          {error ? (
            <p className="fw-note" style={{ color: "#b42318" }}>
              {error}
            </p>
          ) : null}

          {locations.length === 0 ? (
            <p className="fw-note" style={{ marginBottom: 0 }}>
              У вас пока нет локаций, которые можно продлить. Создайте мир
              кнопкой выше — за это спишется одна награда.
            </p>
          ) : (
            <form onSubmit={onSubmit}>
              <div className="fw-fld">
                <label htmlFor="grant-location">Локация</label>
                <select
                  id="grant-location"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  disabled={submitting || available < 1}
                >
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>

              {selected ? (
                <p className="fw-note">Оплачено до: {paidTillLabel}</p>
              ) : null}

              <div className="fw-fld">
                <label htmlFor="grant-months">На сколько месяцев продлить</label>
                <input
                  id="grant-months"
                  type="number"
                  min={1}
                  max={Math.max(1, available)}
                  step={1}
                  value={months}
                  onChange={(e) => setMonths(e.target.value)}
                  disabled={submitting || available < 1}
                />
              </div>

              <div className="fw-form-acts">
                <button
                  type="submit"
                  className="fw-btn"
                  disabled={submitting || available < 1}
                >
                  {submitting ? "Продление…" : "Продлить"}
                </button>
              </div>
            </form>
          )}
        </section>
      )}
    </div>
  );
};

export default GrantExchangePage;
